
import { useState, useEffect } from 'react';
import { propertyViewsService } from '@/services/propertyViewsService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const usePropertyViews = (propertyId: string) => {
  const [viewCount, setViewCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  const fetchViewCount = async () => {
    try {
      setIsLoading(true);
      const count = await propertyViewsService.getPropertyViewCount(propertyId);
      setViewCount(count);
    } catch (error) {
      console.error('Error fetching view count:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const trackView = async () => {
    try {
      // Pass user ID if available for better tracking
      await propertyViewsService.trackView(propertyId, user?.id);
      // Refetch count after tracking
      fetchViewCount();
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchViewCount();
    }
  }, [propertyId]);

  // Set up real-time subscription for view count changes
  useEffect(() => {
    if (!propertyId) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel(`property-views-${propertyId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'property_views',
            filter: `property_id=eq.${propertyId}`
          },
          () => {
            fetchViewCount();
          }
        )
        .subscribe();
    } catch (error) {
      console.warn('Failed to subscribe to property views (Safari security restriction?):', error);
    }

    return () => {
      try {
        if (channel) supabase.removeChannel(channel);
      } catch (error) {
        console.warn('Error cleaning up property views subscription:', error);
      }
    };
  }, [propertyId]);

  return { viewCount, isLoading, trackView, refetch: fetchViewCount };
};
